import { ApolloClient, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createHttpLink } from "@apollo/client/link/http";
import { v4 as uuidv4 } from "uuid";

import { refreshPortalAccess } from "../auth/refreshPortalAccess";
import { GRAPHQL_ENDPOINT } from "../config";
import { connectionMerge } from "./apolloCacheMerge";
import { getAuthData } from "./authStorage";
import { logout } from "./logout";

export const tokenErrors = ["jwt expired", "jwt malformed", "invalid signature", "user-not-found", "mfa-required"];

const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Create auth link to add token dynamically
// Correlation id the backend echoes into its log lines and Postgres application_name (NG-9454).
// The admin bundle cannot import TypeScript from packages/shared, so the id is minted here.
const REQUEST_ID_HEADER = "x-request-id";
const REQUEST_ID_LENGTH = 12;
const newRequestId = (): string => uuidv4().replace(/-/g, "").slice(0, REQUEST_ID_LENGTH);

const authLink = setContext((_, { headers }) => {
  const token = getAuthData()?.token;
  const traced = { ...headers, [REQUEST_ID_HEADER]: newRequestId() };

  return token
    ? {
        headers: {
          ...traced,
          authorization: `Bearer ${token}`,
        },
      }
    : { headers: traced };
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors?.some(error => error.message === "Permission denied" || error.extensions?.code === "FORBIDDEN")) {
    refreshPortalAccess();
  }
  if (graphQLErrors && tokenErrors.includes(graphQLErrors[0].message)) {
    logout();
  }
});

export const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          placetelOpeningHours: {
            merge: false,
          },
        },
      },
      Organization: {
        fields: {
          organizationPatients: {
            merge: connectionMerge,
          },
        },
      },
      Location: {
        fields: {
          patients: {
            merge: connectionMerge,
          },
          locationPractitioners: {
            merge: connectionMerge,
          },
          devices: {
            merge: connectionMerge,
          },
        },
      },
    },
  }),
});
